import React, { useState, useEffect, useCallback } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

import Swal from 'sweetalert2';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Container from '@mui/material/Container';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import InputLabel from '@mui/material/InputLabel';
import OutlinedInput from '@mui/material/OutlinedInput';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import VisibilityOffOutlined from '@mui/icons-material/VisibilityOffOutlined';
import VisibilityOutlined from '@mui/icons-material/VisibilityOutlined';

import brand from 'contexts/brand';

import { loginStyle } from './styles/LoginStyle';

import humanDr from 'images/human_dr.png';
import vetDr from 'images/vet_dr.png';
import humanLogo from 'images/human_logo.png';
import vetLogo from 'images/vet_logo.png';


const LoginDoctors = () => {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [password, setPassword] = useState('');
  const [reCaptchaToken, setRecaptchaToken] = useState();
  const [showPassword, setShowPassword] = useState(false);
  const [username, setUsername] = useState('');

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const handleMouseUpPassword = (event) => {
    event.preventDefault();
  };

  const handleReCaptchaVerify = useCallback(async () => {
    if (!executeRecaptcha) {
      setRecaptchaToken(undefined);
      console.error('Execute recaptcha not yet available');
      return;
    }

    const token = await executeRecaptcha('login_page');
    setRecaptchaToken(token);
    return token;
  }, [executeRecaptcha]);

  useEffect(() => {
    handleReCaptchaVerify();
  }, [handleReCaptchaVerify]);

  const submit = async (submitEvent) => {
    submitEvent.preventDefault();

    setLoading(true);

    let token = reCaptchaToken;
    if (!token) {
      try {
        token = await handleReCaptchaVerify();
      } catch(err) {
        window.alert('Erro ao verificar captcha. Tente novamente.');
      }
    }

    const payload = {
      username,
      password,
      'g-captcha-response': reCaptchaToken
    };

    axios.post('/external/resultsDoctors', payload)
      .then((response) => {
        localStorage.setItem('authSession', JSON.stringify(response.data.user));

        navigate('/doctors/dashboard');
      }).catch((error) => {
        const text = error?.response?.data?.error?.description || 'Tente novamente mais tarde';

        handleReCaptchaVerify();

        Swal.fire({
          type: 'error',
          title: 'Ocorreu um erro!',
          text: text,
          heightAuto: false,
        });

        setLoading(false);
      });
  };

  if (!reCaptchaToken || loading) {
    return (
      <Container
        maxWidth='sm'
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%'
        }}
      >
        <CircularProgress sx={{mb: 3}} />
      </Container>
    )
  };

  return (
    <>
      <Box style={{ position: 'absolute', top: '50%', transform: 'translateY(-50%)' }}>
        <Box component='img' src={brand === 'vet' ? vetDr : humanDr} sx={{width: '48vw'}}/>
      </Box>

      <Box
        sx={{
          ...loginStyle.container,
          padding: { xs: '14vh 8.5vw 13.5vh', sm: '15vh 9vw 12.5vh' }
        }}
      >
        <a href='https://drtis.com.br'>
          <Box
            component='img'
            src={brand === 'vet' ? vetLogo : humanLogo} alt={brand === 'vet' ? 'Dr. Nuvem' : 'Dr. Tis'}
            sx={loginStyle.logo}
          />
        </a>

        <Box component='form' onSubmit={submit} sx={loginStyle.formComplete}>
          <Typography variant='h1'> Portal de Resultados </Typography>

          <Typography variant='h5'>
            Consulte imagens ou laudos dos exames usando 
            o código fornecido pelo laboratório, clínica ou hospital:
          </Typography>

          <TextField
            fullWidth
            sx={loginStyle.input}
            onChange={(e) => setUsername(e.target.value)}
            defaultValue={username}
            name='username'
            label='Usuário:'
          />

          <FormControl sx={loginStyle.input}>
            <InputLabel>Senha:</InputLabel>
            <OutlinedInput
              fullWidth
              type={showPassword ? 'text' : 'password'}
              onChange={(e) => setPassword(e.target.value)}
              endAdornment={
                <InputAdornment position='end'>
                  <IconButton
                    onClick={handleClickShowPassword}
                    onMouseDown={handleMouseDownPassword}
                    onMouseUp={handleMouseUpPassword}
                    edge='end'
                    variant='outlined'
                  >
                    {showPassword
                      ? <VisibilityOutlined color='secondary' />
                      : <VisibilityOffOutlined color='secondary'/>
                    }
                  </IconButton>
                </InputAdornment>
              }
              label='Senha'
            />
          </FormControl>

          <Button
            variant='contained'
            color='primary'
            type='submit'
            fullWidth
            sx={loginStyle.button}
            disabled={!username || !password}
          >
            Acessar resultados
          </Button>
        </Box>

        <a href='https://drtis.com.br/pacs/'>
          <Typography variant='h6'> Quer saber mais sobre PACS? </Typography>
        </a>
      </Box>
    </>
  );
};

export default LoginDoctors;